import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import CircularProgress from '@mui/material/CircularProgress';
import classes from './modals.module.scss';
import MainButton from '../../../../common/buttons/MainButton';
import { tneAxiosClient } from '../../../../../api/apiSettings';
import OutlinedCheckBox from '../../../../common/Form/OutlinedCheckBox/OutlinedCheckBox';
import { PageContext } from '../../../../../context/context';
import { generalEnums, submissionEnum } from '../../../../../utils/enums';
import colors from '../../../../../scss/colors';
import CustomMonthPicker from '../../../../common/CustomMonthPicker/CustomMonthPicker';
import { calendarTypes } from '../../../../../utils/constants';
import CustomCalendarPicker from '../../../../common/CustomWeekPicker/CustomCalendarPicker';
import { basicErrorHandler, buildMonth, getFirstDayOFMonth } from '../../../../../utils/helpers';

const shortDays = ['Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun'];

function SavedPopUp() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className={classes.popupContainer}>
      <p className={classes.popupLabel}>{t('dashboard.timesheet.savedmessage')}</p>
      <MainButton rounded forceWidth="244px" action={() => navigate('/dashboard/create-timesheet')}>
        {t('dashboard.timesheets.newbuttontxt')}
      </MainButton>
      <MainButton rounded outlined forceWidth="244px" action={() => navigate('/dashboard/timesheets')}>
        {t('dashboard.timesheet.gotodashboard')}
      </MainButton>
    </div>
  );
}

function UnsavedChanges({ saveHandler, closeHandler, rejectPreventing }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [canClose, setCanClose] = useState(false);
  const ctx = useContext(PageContext);

  useEffect(() => {
    if (canClose) {
      if (ctx.pageType === generalEnums.TIMESHEET) {
        navigate('/dashboard/timesheets');
      } else {
        navigate('/dashboard/expenses');
      }
    }
  }, [canClose]);

  return (
    <div className={classes.popupContainer}>
      <p className={classes.popupLabel}>{t('dashboard.timesheet.unsavedmessage')}</p>
      <div className={classes.actionContainer}>
        <MainButton
          rounded
          outlined
          forceWidth="164px"
          action={() => {
            rejectPreventing(false);
            setCanClose(true);
          }}
        >
          {t('common.general.discard')}
        </MainButton>
        <MainButton
          rounded
          forceWidth="164px"
          action={() => {
            saveHandler();
            closeHandler();
          }}
        >
          {t('common.general.save')}
        </MainButton>
      </div>
    </div>
  );
}

function DeletePopUp({ deleteHandler, closeHandler, submissionType = null }) {
  const { t } = useTranslation();

  return (
    <div className={classes.popupContainer}>
      <p className={classes.popupLabel}>
        {submissionType === submissionEnum.MONTHLY
          ? t('dashboard.timesheet.monthlydeletemessage')
          : t('dashboard.timesheet.deletemessage')}
      </p>
      <MainButton rounded forceWidth="244px" action={deleteHandler}>
        {t('common.general.delete')}
      </MainButton>
      <MainButton rounded outlined forceWidth="244px" action={closeHandler}>
        {t('common.general.back')}
      </MainButton>
    </div>
  );
}

function FinalModal({ message, queryParams }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [id, setId] = useState(null);

  const getNextDashboardId = async () => {
    try {
      const response = await tneAxiosClient.get(
        `v1/get-timesheets-by-approverId/?userId=${localStorage.getItem('UserId')}&Status=Pending`
      );

      if (response.data.Data?.[0]?.Id) {
        setId(response.data.Data[0].Id);
      }
    } catch (e) {
      setId(null);
    }
  };

  useEffect(() => {
    getNextDashboardId();
  }, []);

  return (
    <div className={classes.popupContainer}>
      <p className={classes.popupLabel}>{message}</p>
      {id && (
        <MainButton rounded forceWidth="244px" action={() => navigate(`/dashboard/timesheet/${id}`)}>
          {t('dashboard.timesheet.nexttimesheet')}
        </MainButton>
      )}
      <MainButton rounded outlined forceWidth="244px" action={() => navigate(`/dashboard/timesheets${queryParams}`)}>
        {t('dashboard.timesheet.gotodashboard')}
      </MainButton>
    </div>
  );
}

function ConfirmationModal({ confirm, pending }) {
  const { t } = useTranslation();

  return (
    <div className={classes.popupContainer}>
      <p className={classes.popupLabel}>{t('dashboard.timesheet.approveconfirmation')}</p>
      <MainButton rounded forceWidth="244px" action={confirm}>
        {pending ? <CircularProgress size={20} sx={{ color: colors.WHITE }} /> : t('common.general.approve')}
      </MainButton>
    </div>
  );
}

function CopyModal({ timesheet }) {
  const { t } = useTranslation();

  const firstDateofMonth = new Date(
    timesheet.TimesheetDetails[0].TimesheetHoursByDates.find((item) => new Date(item.Day).getDate() === 1)?.Day
  );

  const submissionType = timesheet.TimesheetType.split('-')[0].toLowerCase();

  const [newDate, setNewDate] = useState(
    dayjs(
      timesheet.TimesheetDetails[0].TimesheetHoursByDates[
        timesheet.TimesheetDetails[0].TimesheetHoursByDates.length - 1
      ].Day
    ).add(1, 'day')
  );
  const [month, setMonth] = useState(firstDateofMonth.getMonth() + 1);
  const [year, setYear] = useState(firstDateofMonth.getFullYear());

  const onSubmit = async () => {
    const date =
      submissionType === submissionEnum.WEEKLY
        ? new Date(newDate.toString())
        : getFirstDayOFMonth(new Date(year, month).toString());

    let startDate = '';

    if (date.getDay() === 0) {
      startDate = dayjs(date.toString()).add(-7, 'day').day(2).toString();
    } else {
      startDate = dayjs(date.toString()).day(2).toString();
    }

    let resultData = {};

    if (submissionType === submissionEnum.WEEKLY) {
      resultData = {
        ...timesheet,
        TimesheetDetails: timesheet.TimesheetDetails.map((ts) => ({
          ...ts,
          WorkTypeId: ts.WorkType.Id,
          TimesheetHoursByDates: Array.from({ length: 7 }, (v, k) => k).map((increaser) => ({
            TotalHrs: '0',
            Day: dayjs(startDate)
              .add(increaser - 1, 'day')
              .format('YYYY-MM-DD')
          }))
        })),
        TimesheetPerDiem: Array.from({ length: 7 }, (v, k) => k).map((increaser) => ({
          IsPerDiem: false,
          Date: dayjs(startDate)
            .add(increaser - 1, 'day')
            .format('YYYY-MM-DD')
        }))
      };
    } else {
      const actualDate = dayjs(date);
      const newMonth = buildMonth(actualDate);
      newMonth.shift();
      const datesToAdd = newMonth.flat();
      resultData = {
        ...timesheet,
        TimesheetDetails: timesheet.TimesheetDetails.map((ts) => ({
          ...ts,
          WorkTypeId: ts.WorkType.Id,
          TimesheetHoursByDates: [
            ...Array.from({ length: 7 }, (v, k) => k).map((increaser) => ({
              TotalHrs: '0',
              Day: dayjs(startDate)
                .add(increaser - 1, 'day')
                .format('YYYY-MM-DD')
            })),
            ...datesToAdd.map((day) => ({
              TotalHrs: '0',
              Day: dayjs(day).format('YYYY-MM-DD')
            }))
          ]
        })),
        TimesheetPerDiem: [
          ...Array.from({ length: 7 }, (v, k) => k).map((increaser) => ({
            IsPerDiem: false,
            Date: dayjs(startDate)
              .add(increaser - 1, 'day')
              .format('YYYY-MM-DD')
          })),
          ...datesToAdd.map((day) => ({
            IsPerDiem: false,
            Date: dayjs(day).format('YYYY-MM-DD')
          }))
        ]
      };
    }

    try {
      const response = await tneAxiosClient.post('/v1/timesheet', resultData);
      window.location = `${window.location.origin}/dashboard/timesheet/${response.data.Data.Id}?flag=created`;
    } catch (e) {
      basicErrorHandler(e.message);
    }
  };

  return (
    <div className={`${classes.containerNoPadding} ${classes.withPadding}`}>
      <p className={classes.headLabel}>
        {submissionType === submissionEnum.MONTHLY
          ? t('dashboard.createtimesheet.steps.selectmonth')
          : t('dashboard.createtimesheet.steps.selectweek')}
      </p>
      <div className={classes.calendarWrapper}>
        {submissionType === submissionEnum.MONTHLY ? (
          <CustomMonthPicker
            month={month}
            year={year}
            setMonth={setMonth}
            setYear={setYear}
            backgroundColor={colors.WHITE}
            disabledBeforeIncluded={{ month: firstDateofMonth.getMonth(), year: firstDateofMonth.getFullYear() }}
          />
        ) : (
          <CustomCalendarPicker
            userDate={newDate}
            setUserDate={(date) => {
              setNewDate(date);
            }}
            calendarType={calendarTypes.WEEK}
            backgroundColor={colors.WHITE}
            disabledBeforeIncluded={dayjs(
              timesheet.TimesheetDetails[0].TimesheetHoursByDates[
                timesheet.TimesheetDetails[0].TimesheetHoursByDates.length - 1
              ].Day
            ).add(1, 'day')}
          />
        )}
      </div>
      <MainButton rounded forceWidth="244px" action={onSubmit}>
        {t('common.general.next')}
      </MainButton>
    </div>
  );
}

function RejectModal() {
  const { t } = useTranslation();

  return (
    <div className={classes.popupContainer}>
      <p className={classes.popupLabel}>{t('dashboard.timesheet.rejectmessagereq')}</p>
    </div>
  );
}

function EditPerDiemModal({ dataSet }) {
  const { data, isUpdate, submissionType, currentMonth, savePDHandler, closePDHandler, deletePDHandler } = dataSet;
  const { t } = useTranslation();

  const [hasChanges, setHasChanges] = useState(false);
  const [editableData, setEditableData] = useState(data);

  const changeHandler = (index, value) => {
    if (!hasChanges) setHasChanges(true);
    setEditableData(editableData.map((pd, i) => (index === i ? { ...pd, IsPerDiem: value } : pd)));
  };

  const monthlyChangeHandler = (date, newValue) => {
    if (!hasChanges) setHasChanges(true);
    setEditableData(editableData.map((pd) => (dayjs(date).isSame(pd.Date) ? { ...pd, IsPerDiem: newValue } : pd)));
  };

  return (
    <div
      className={`${classes.containerNoPadding} ${
        submissionType === submissionEnum.MONTHLY ? classes.withPadding : ''
      }`}
    >
      <p className={classes.popupLabelNoMargin}>
        {isUpdate ? t('common.general.update') : t('common.general.add')} {t('dashboard.timesheet.perdiem')}
      </p>
      <div className={classes.perDiemContainer}>
        {submissionType === submissionEnum.WEEKLY ? (
          editableData.map((item, index) => (
            <div className={classes.pdItem} key={item.Date}>
              <p>{dayjs(item.Date).format('ddd')}</p>
              <p>{dayjs(item.Date).format('D')}</p>
              <OutlinedCheckBox
                value={item.IsPerDiem}
                onChange={() => changeHandler(index, !item.IsPerDiem)}
                disabled={dayjs(item.Date).month() !== currentMonth && submissionType === submissionEnum.MONTHLY}
              />
            </div>
          ))
        ) : (
          <section className={classes.wrapper}>
            {shortDays.map((dayName, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <p key={index} className={classes.dayTitle}>
                {dayName}
              </p>
            ))}
            {editableData.map((item) =>
              new Date(item.Date).getMonth() === currentMonth ? (
                <div key={item.Date} className={classes.pdItemMonthly}>
                  <p>{new Date(item.Date).getDate()}</p>
                  <OutlinedCheckBox
                    value={item.IsPerDiem}
                    onChange={() => monthlyChangeHandler(item.Date, !item.IsPerDiem)}
                    size="28px"
                  />
                </div>
              ) : (
                <p key={item.Date}>&nbsp;</p>
              )
            )}
          </section>
        )}
      </div>
      <div className={classes.actionContainer}>
        <MainButton
          rounded
          outlined
          forceWidth="129px"
          variant="disabled"
          action={isUpdate ? () => deletePDHandler(editableData) : closePDHandler}
        >
          {isUpdate ? t('common.general.delete') : t('common.general.cancel')}
        </MainButton>
        <MainButton
          rounded
          forceWidth="129px"
          variant={hasChanges ? 'primary' : 'disabled'}
          disabled={!hasChanges}
          action={() => savePDHandler(editableData)}
        >
          {isUpdate ? t('common.general.update') : t('common.general.save')}
        </MainButton>
      </div>
    </div>
  );
}

export {
  SavedPopUp,
  UnsavedChanges,
  DeletePopUp,
  FinalModal,
  ConfirmationModal,
  RejectModal,
  EditPerDiemModal,
  CopyModal
};
